/* eslint-disable no-unused-vars */
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../../enums";
import { IOrganizations } from "../../../types/Organisation";
import { IApiResponse } from "../../models";
import { EContractActions } from "../contractActions/enum";
import { IUserInfoResponseData } from "../user/models";

export enum EContractType {
  MILESTONE_CONTRACT = "MILESTONE_CONTRACT",
  BROKERAGE_CONTRACT = "BROKERAGE_CONTRACT",
  COMPLEX_CONTRACT = "COMPLEX_CONTRACT",
  QUICK_TRANSACTION = "QUICK_TRANSACTION",
  VEHICLES = "VEHICLES",
  DEVICES = "DEVICES",
  FURNITURE = "FURNITURE",
  REAL_ESTATES = "REAL_ESTATES",
  SERVICES = "SERVICES",
  ANIMALS = "ANIMALS",
}
export enum ESenderRole {
  PROVIDER = "PROVIDER",
  CUSTOMER = "CUSTOMER",
  BROKER = "BROKER",
  ADMIN = "ADMIN",
  CLIENT_ADMIN = "CLIENT_ADMIN",
  SERVICE_PROVIDER = "SERVICE_PROVIDER",
}
export interface ItemDetailsDTO {
  title: string;
  description: string;
  field?: IFieldDTO[];
  images: string[];
  attachments: string[];
  category: string;
}
export interface IFieldDTO {
  key: string;
  value: string;
}
export enum ECurrency {
  SAR = "SAR",
}
export enum EShipmentType {
  SHIPMENT_COMPANY = "SHIPMENT_COMPANY",
  FACE_TO_FACE = "FACE_TO_FACE",
  VIRTUAL = "VIRTUAL",
  NONE = "NONE",
}
export interface IShipmentDTO {
  number: string;
  provider: string;
  fee: number;
  currency: ECurrency;
  feePayee?: string;
  info?: string;
  type?: EShipmentType;
  courierPhoneNumber?: string;
  trackingCode?: string;
  shipmentRequestId?: string;
}
export enum EReturnPolicy {
  RETURN_SPECIFICATIONS_VIOLATED = "RETURN_SPECIFICATIONS_VIOLATED",
  RETURN_WITHOUT_REASONS_DURING_EXAMINATION_PERIOD = "RETURN_WITHOUT_REASONS_DURING_EXAMINATION_PERIOD",
  NO_RETURN = "NO_RETURN",
}
export enum EFeePayee {
  PROVIDER = "PROVIDER",
  CUSTOMER = "CUSTOMER",
  BOTH = "BOTH",
}
export interface IReturnItemDTO {
  returnReason?: string | null;
  returnReasonDesc?: string | null;
  images?: string[] | null;
}

export type Escalations = {
  createdAt: Date;
  escalationType: EContractActions;
};
export interface Transactions {
  createdAt: string;
  id: string;
  type: string;
  fromUserId?: string;
  referenceTransactionId: string;
  status: string;
  paymentMethod?: string;
  beneficiaryId: string;
}

export enum ContractClassification {
  STOCK_CONTRACT = "STOCK_CONTRACT",
  STOCK_CONTRACT_HARAJ = "STOCK_CONTRACT_HARAJ",
  BASIC = "BASIC",
  BASIC_HARAJ = "BASIC_HARAJ",
  BROKER_CONTRACT = "BROKER_CONTRACT",
}
export interface UserProfile {
  id: number;
  userId?: string;
  userIdentification?: number;
  phoneNumber: string;
  nationalIdVerified: true;
  role?: ESenderRole;
  amount?: number;
  feePercentage?: number;
  fee?: number;
  sudoName?: string;
  sudoRole?: string;
  status?: EPARTY_STATUS;
  arbitrator?: boolean;
  isSender?: boolean;
  isCompany?: boolean;
  companyUnifiedNumber?: string;
  companyName?: string;
  profile: {
    firstName: string;
    lastName: string;
    fullName: string;
    languageCode: string;
    photoUrl: string;
    feedbackOverallRatingScore: number;
  };
}

export type PartiesProfiles = {
  [key in ESenderRole]: Partial<UserProfile>[];
};

export interface CashOutAmount {
  id?: number;
  userIdentification?: string;
  amountDue?: number;
  feePercentage?: number;
  fee?: number;
  cashOutAmount: number;
  manualWithDrawCashOut?: boolean;
  settlementAmountStatus: string;
  multiCashOut?: boolean;
}

export enum FeeCalculationType {
  FLAT = "FLAT",
  PERCENTAGE = "PERCENTAGE",
}

export enum AddOnFeeCategory {
  SHIPPING_FEES = "SHIPPING_FEES",
  SERVICE_FEES = "SERVICE_FEES",
  EXTRA_AMOUNT = "EXTRA_AMOUNT",
}

export interface AddOnFees {
  amount: number;
  payee: string;
  feeType: FeeCalculationType;
  type: AddOnFeeCategory;
}

export interface OperationsMetaData {
  cashIn?: string;
  category?: string;
  operationStatus?: string;
  operationalType?: string;
  escalation?: string;
  operationResult?: string;
  commisionBearer?: string;
  shippingCompany?: string;
  cashOut?: string;
  cashOutOutSystem?: string;
  cashOutOutType?: string;
  organization?: string;
  organizationWaffyFee?: string;
  organizationOperationResult?: string;
  comments?: string;
}

export interface IContractDetailsResponseData {
  cashOutAmountList?: CashOutAmount[];
  addonFeesList?: AddOnFees[];
  activeChildrenCount?: number;
  organizationCode?: string;
  organization?: Partial<IOrganizations>;
  operationsMetaData?: OperationsMetaData;
  activeChildrenTotalAmount?: number;
  activeChildrenPartiesCount?: number;
  contractDefinitionId?: string;
  brokerFee?: number;
  id: string;
  type: EContractType;
  currentStep?: string;
  contractClassification: ContractClassification;
  isAcceptable?: boolean;
  isInspectable?: boolean;

  parentContractId?: string;
  milestones?: IContractDetailsResponseData[];

  shortId: string;
  originalItemPrice: number;
  paymentMethods?: Partial<Record<EPaymentType, EPaymentBrand[]>>;

  senderId: string;
  brokerId: string;
  senderPhoneNumber: string;
  senderRole: ESenderRole;
  isParent?: boolean;
  parentId?: string;

  itemDetail: ItemDetailsDTO;
  itemPrice: number;
  unitPrice?: number;
  fee: number;
  feePercentage: number;
  totalAmount: number;
  currency: ECurrency;
  feePayee: EFeePayee;
  deadLine?: string;
  cancellationConditions?: string;
  acceptanceConditions?: string;
  hasVoucher?: boolean;

  address?: {
    city: string;
    countryCode: string;
    district: string;
    id: number;
    postalCode: string;
    street: string;
  };
  metaData: {
    [key: string]: string | number | boolean;
    sellerRequiresShippingInfo: boolean;
  };

  shipment?: IShipmentDTO | null;
  deliveryInDays: number;
  partiesProfiles?: PartiesProfiles;
  parties?: Partial<UserProfile>[];
  returnPolicy: EReturnPolicy;
  inspectionPeriod: number;
  returnFee: number;
  returnFeePayee?: EFeePayee | null;
  transactions: Transactions[];
  receiverId: string;
  receiverPhoneNumber?: string | null;
  receiverRole?: ESenderRole;

  ReturnItemDTO?: IReturnItemDTO | null;
  returnShipment?: IShipmentDTO | null;

  waffyTermsAccepted: boolean;

  status: ECONTRACT_STATUS;
  createdAt: Date | string;

  escalations?: IContractEscalationDetails[];
  originalQuantity?: number;
  quantity?: number;

  provider: IUserInfoResponseData;
  customer?: IUserInfoResponseData;

  customerAdditionalInfo: string;
  customerShippingAddress: string;

  shippable?: boolean;
  buyerOnly?: boolean;
  sourceContractId?: string;
}
export interface IContractEscalationDetails {
  ticketId: string;
  reason: string;
  message: string;
  userId: string;
  userRole: ESenderRole;
  escalationType: EEscalaionType;
  resolved: boolean;
  createdAt: string;
  resolvedAt: string;
}
export enum EEscalaionType {
  CUSTOMER_DENY_DELIVERY = "CUSTOMER_DENY_DELIVERY",
  PROVIDER_ESCALATE_DELIVERY = "PROVIDER_ESCALATE_DELIVERY",
  CUSTOMER_REJECT_ITEM = "CUSTOMER_REJECT_ITEM",
  PROVIDER_ESCALATE_ITEM = "PROVIDER_ESCALATE_ITEM",
  CUSTOMER_CHECK_ESCALATION = "CUSTOMER_CHECK_ESCALATION",
  PROVIDER_CHECK_ESCALATION = "PROVIDER_CHECK_ESCALATION",
}

export interface IMilestonesPartiesData {
  [key: string]: {
    phoneNumber?: string | number;
    userId?: string | number;
    companyUnifiedNumber?: string;
    companyName?: string;
    isCompany?: boolean;
    role: ESenderRole;
    sudoRole?: string;
    sudoName?: string;
    amount: string | number;
  }[];
}

export enum EPaymentType {
  HYPER_PAY = "HYPER_PAY",
  MANUAL_BANK_TRANSFER = "MANUAL_BANK_TRANSFER",
  MOYASAR = "MOYASAR",
  TABBY = "TABBY",
  APPLE_PAY = "APPLE_PAY",
  STCPAY = "STCPAY",
  CONTRACT_TRANSFER = "CONTRACT_TRANSFER",
}

export enum EPaymentBrand {
  MADA = "MADA",
  VISA = "VISA",
  MASTER = "MASTER",
  AMEX = "AMEX",
  APPLE_PAY = "APPLE_PAY",
  TABBY = "TABBY",
  BANK_TRANSFER = "BANK_TRANSFER",
  STCPAY = "STCPAY",
  CONTRACT_TRANSFER = "CONTRACT_TRANSFER",
}

export interface IContractDetailsBody {
  type: EContractType;
  senderRole: ESenderRole;
  draft?: boolean;
  itemDetail: Partial<ItemDetailsDTO>;
  itemPrice: number | string;
  currentStep?: string;
  currency: ECurrency;
  feePayee?: EFeePayee;
  shipment: {
    type: EShipmentType;
    fee?: number;
    currency: ECurrency;
  };
  paymentMethods?: Partial<Record<EPaymentType, EPaymentBrand[]>>;
  brokerFee?: number | string;
  returnPolicy: EReturnPolicy;
  inspectionPeriod?: number;
  isInspectable?: boolean;
  isAcceptable?: boolean;
  deliveryInDays?: number;
  returnFee?: number;
  deadLine?: string;
  cancellationConditions?: string;
  acceptanceConditions?: string;
  returnFeePayee?: EFeePayee | null;
  receiverId: string | null;
  receiverName: string | null;
  returnItem?: IReturnItemDTO;
  contractClassification: ContractClassification;
  returnShipment?: {
    type: EShipmentType;
    fee: number;
    currency: ECurrency;
  };
  waffyTermsAccepted: boolean;
  interactiveContract?: boolean;
  quantity?: number;
  clientReferenceId?: string;
  buyerOnly?: boolean;
  sourceContractId?: string;
  metaData?: {
    [key: string]: string | number | boolean;
  };
}

export interface ITrasnactionObject {
  type: ETrasactionsType;
  id: string;
  createdAt: string;
  referenceTransactionId: string;
}
export enum ETrasactionsType {
  CUSTOMER_PAY = "CUSTOMER_PAY",
  REFUND_BUYER = "REFUND_BUYER",
  CLOSE_RELEASE_TO_PROVIDER = "CLOSE_RELEASE_TO_PROVIDER",
  RELEASE_TO_PROVIDER = "RELEASE_TO_PROVIDER",
  RELEASE_TO_SELLER = "RELEASE_TO_SELLER",
  CLOSE_RELEASE_TO_SELLER = "CLOSE_RELEASE_TO_SELLER",
  MANUAL_BANK_TRANSFER = "MANUAL_BANK_TRANSFER",
  CASH_OUT = "CASH_OUT",
}
export interface IContractDetailsResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}

export interface IContractActionsResponse extends IApiResponse {
  data: IContractActionsResponseData[];
}

export interface IUpdateContractRequestBody {
  title: string;
  description: string;
  fields?: {
    key: string;
    value: string;
  }[];
  images?: string[];
}

export interface IContractListResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}
export interface IContractDetailsResponseData {
  content: IContractDetailsResponseData[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
}

export interface IContractActionsResponseData {
  action: string;
  actionStatus: string;
  actorId: string;
  actorRole: ESenderRole;
  createdAt: Date;
  to: string;
}
export interface IPostFeedBackRequest {
  contractId: string;
  shortContractId: string;
  contractTitle: string;
  receiverId?: string;
  contractCustomerId?: string;
  providerId?: string;
  providerRole: string;
  ratingScore: number;
  comment?: string;
}

export interface DiscountInfo {
  amountLimit: number;
  percentOff: number;
}
export interface voucherContent {
  amount: number;
  discountAmount: number;
  domainId: string;
  domainType: string;
  externalRedemptionId: string;
  id: number;
  status: string;
  totalAmount: number;
  userId: string;
  voucherCode: string;
  discountInfo: DiscountInfo;
}

export interface IVoucherResponseData {
  content: voucherContent[];
}

export interface IContractDetailsResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}

export interface IContractActionsResponse extends IApiResponse {
  data: IContractActionsResponseData[];
}

export interface IVoucherResponse extends IApiResponse {
  data: IVoucherResponseData;
}

export interface IContractStatsResponse extends IApiResponse {
  data: IContractStats;
}

export interface IContractStats {
  activeUsersStats: {
    registered: number;
    unRegistered: number;
  };
  paidUsersStats: {
    paid: number;
    unPaid: number;
  };
  paymentStats: {
    amountPaid: number;
    amountUnpaid: number;
  };
}

// CAS.E0001 -> Sum of Amounts for PROVIDERS is not same as ItemPrice for the contract with Id %s
// CAS.E0002 -> Sum of Amounts for CUSTOMERS is greater than ItemPrice for the contract with Id %s
// CAS.E0003 -> Sum of milestones item prices: %d is larger than the unallocated item price of parent contract %d
// CAS.E0004 -> isSender not exists
// CAS.E0005 -> Contract with Id %s has less than two parties.
// CAS.E0006 -> Contract with Id %s has more/less than one sender.
export const PUBLISH_ERROR_CODES = {
  "CAS.E0001": "lbl_sum_providers_error",
  "CAS.E0002": "lbl_sum_customers_error",
  "CAS.E0003": "lbl_sum_milestones_price_error",
  "CAS.E0004": "lbl_no_sender_error",
  "CAS.E0005": "lbl_parties_error",
  "CAS.E0006": "lbl_sender_count_error",
};

export interface ISettlementRequest {
  id: string;
  sourceContractId?: string;
  destinationContractId: string;
  destinationContractShortId?: string;
  amount: number;
  status: string;
  contractTitle?: string;
  createdByActorId?: string;
  createdByActorRole?: string | null;
  createdAt: string;
  decidedByActorId?: string | null;
  decidedByActorRole?: string | null;
  decidedAt?: string | null;
  executionTransactionId?: string | null;
  rejectionReason?: string | null;
}

export interface ISettlementRequestsResponse {
  data: Record<string, ISettlementRequest[]>;
}

export const ERROR_PATTERNS = {
  "CAS.E0001": /contract with Id (\w+)/,
  "CAS.E0002": /contract with Id (\w+)/,
  "CAS.E0003": /item prices: (\d+).*contract (\d+)/,
  "CAS.E0004": /isSender not exists/,
  "CAS.E0005": /Contract with Id (\w+) has less than two parties/,
  "CAS.E0006": /Contract with Id (\w+) has more\/less than one sender/,
};
